import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { isFuture } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Info } from '@sorare/core/src/atoms/icons/Info';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelS } from '@sorare/core/src/atoms/typography';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { NoCardEntryInfoDialog } from '@sorare/core/src/components/noCardEntry/NoCardEntryInfoDialog';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useNoCardRoute } from '@sorare/core/src/hooks/config/useNoCardRoute';
import { glossary } from '@sorare/core/src/lib/glossary';

import { So5NoCardEntryRegisterDialog } from 'components/noCardRoute/So5NoCardEntryRegisterDialog';

const Root = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-nd-50);

  > button {
    align-self: start;
  }
`;

const POOL_DETAILS_EXAMPLE: {
  [key in Sport]:
    | {
        gameWeek: number;
        limited: number;
        rare: number;
        superRare: number;
        unique: number;
      }
    | undefined;
} = {
  [Sport.FOOTBALL]: {
    gameWeek: 377,
    limited: 780617,
    rare: 256231,
    superRare: 26141,
    unique: 2582,
  },
  [Sport.BASEBALL]: undefined,
  [Sport.NBA]: undefined,
};

interface Props {
  sport: Sport;
  eventType: So5FixtureEvent;
  alwaysShow?: boolean;
}

export const NoCardEntry = ({
  sport,
  eventType,
  alwaysShow = false,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const {
    noCardRoute: { nextOpenDate, nextCloseDate, emailAddress },
    loading,
  } = useNoCardRoute({ sport, eventType });

  const [mainDialogOpened, setMainDialogOpened] = useState(false);
  const [infoDialogOpened, setInfoDialogOpened] = useState(false);

  if (!currentUser?.noCardRouteEnabled) return null;

  const { so5NoCardRouteOpened } = currentUser;

  if (!alwaysShow && !so5NoCardRouteOpened) return null;

  const showOpensIn = !!nextOpenDate && isFuture(nextOpenDate);
  const showCloseIn =
    !showOpensIn && !!nextCloseDate && isFuture(nextCloseDate);

  return (
    <Root>
      <Vertical gap={0}>
        <SBHorizontal>
          <BodyM as="p" bold>
            <FormattedMessage {...glossary.noCardEntry} />
          </BodyM>
          <LabelS
            as="button"
            onClick={() =>
              so5NoCardRouteOpened
                ? setMainDialogOpened(true)
                : setInfoDialogOpened(true)
            }
            type="button"
          >
            <Info />
          </LabelS>
        </SBHorizontal>
        <LabelS
          as="p"
          color={so5NoCardRouteOpened ? 'var(--c-nd-700)' : 'var(--c-nd-400)'}
        >
          {showOpensIn || showCloseIn ? (
            <Horizontal as="span">
              {so5NoCardRouteOpened ? (
                <LiveDot animate />
              ) : (
                <FontAwesomeIcon icon={faClock} />
              )}
              <span>
                {!!showOpensIn && (
                  <FormattedMessage
                    {...glossary.opensIn}
                    values={{ time: <TimeLeft time={nextOpenDate} /> }}
                  />
                )}
                {!!showCloseIn && (
                  <FormattedMessage
                    {...glossary.closesIn}
                    values={{ time: <TimeLeft time={nextCloseDate} /> }}
                  />
                )}
              </span>
            </Horizontal>
          ) : (
            <>
              {loading ? (
                <>&nbsp;</>
              ) : (
                <FormattedMessage {...glossary.closed} />
              )}
            </>
          )}
        </LabelS>
      </Vertical>
      <So5NoCardEntryRegisterDialog
        sport={sport}
        eventType={eventType}
        emailAddress={emailAddress}
        open={mainDialogOpened}
        onClose={() => setMainDialogOpened(false)}
      />
      <NoCardEntryInfoDialog
        open={infoDialogOpened}
        onClose={() => setInfoDialogOpened(false)}
        emailAddress={emailAddress}
        poolDetail={
          !!POOL_DETAILS_EXAMPLE[sport] && (
            <FormattedMessage
              id="Lobby.CompetitionList.NoCardEntry.poolDetail"
              defaultMessage="For instance, for Game Week {gameWeek}, the pool of players available was composed of {limitedCount} Limited Cards, {rareCount} Rare Cards, {superRareCount} Super Rare Cards, {uniqueCount} Unique Cards."
              values={{
                gameWeek: POOL_DETAILS_EXAMPLE[sport].gameWeek,
                limitedCount: POOL_DETAILS_EXAMPLE[sport].limited,
                rareCount: POOL_DETAILS_EXAMPLE[sport].rare,
                superRareCount: POOL_DETAILS_EXAMPLE[sport].superRare,
                uniqueCount: POOL_DETAILS_EXAMPLE[sport].unique,
              }}
            />
          )
        }
      />
    </Root>
  );
};
